import { dsv } from "d3";
import React, { useState } from "react";
import { Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";

const APP_OPTIONS = [{ key: "olitask-mobile-ui", label: "olitask-mobile-ui" }];

const STATUS_OPTIONS = [
  { key: "DRAFT", label: "Draft" },
  { key: "ACTIVE", label: "Active" },
  { key: "CURRENT", label: "Current" },
  { key: "EXPIRED", label: "Expired" },
  { key: "DISABLED", label: "Disabled" },
];

const AddAppVersionForm = ({ onAdded, onClose, createAppVersion, loading }) => {
  const [version, setVersion] = useState("");
  const [appName, setAppName] = useState("olitask-mobile-ui");
  const [status, setStatus] = useState("DRAFT");
  const [releaseDate, setReleaseDate] = useState("");
  const [description, setDescription] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [buildNumber, setBuildNumber] = useState("");
  const [androidStoreUrl, setAndroidStoreUrl] = useState("");
  const [appleStoreUrl, setAppleStoreUrl] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [isRecommended, setIsRecommended] = useState(false);
  const [isMinimumSupported, setIsMinimumSupported] = useState(false);
  const [updateTitle, setUpdateTitle] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);


  const isFormInvalid =
    !version.trim() ||
    !versionCode ||
    isNaN(versionCode) ||
    !buildNumber ||
    isNaN(buildNumber) ||
    !releaseDate ||
    !updateTitle.trim();

  const handleSubmit = async (e) => {
      e.preventDefault();
      setLocalError(null);

      const trimmedVersion = version.trim();
      const trimmedUpdateTitle = updateTitle.trim();

      if (!trimmedVersion) {
        return setLocalError("Version name is required.");
      }

      if (!versionCode || isNaN(versionCode)) {
        return setLocalError("Version code must be a valid number.");
      }

      if (!buildNumber || isNaN(buildNumber)) {
        return setLocalError("Build number must be a valid number.");
      }

      if (!releaseDate) {
        return setLocalError("Effective date is required.");
      }

      if (!trimmedUpdateTitle) {
        return setLocalError("Update title is required.");
      }

    try {
      setIsSubmitting(true);

      await createAppVersion({
        appName: appName,
        versionName: version,
        versionCode: Number(versionCode),
        buildNumber: Number(buildNumber),
        androidStoreUrl: androidStoreUrl,
        appleStoreUrl: appleStoreUrl,
        forceUpdate: forceUpdate,
        isMinimumSupported: isMinimumSupported,
        isRecommended: isRecommended,
        updateTitle: updateTitle,
        updateMessage: description,
        effectiveFrom: new Date(releaseDate).toISOString(),
      });

      toast.success("App version created");

      onAdded && onAdded();
      onClose && onClose();
    } catch (err) {
      const msg = err.message || "Failed to create app version";
      setLocalError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {localError && (
        <Alert variant="danger" className="mb-3">
          {localError}
        </Alert>
      )}

      <div className="mb-3">
        <Form.Label>App</Form.Label>
        <Form.Select
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          disabled={isSubmitting}
          required
        >
          {APP_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      </div>

      <div className="mb-3">
        <Form.Label>Version Name</Form.Label>
        <Form.Control
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. 1.2.0"
          required
          isInvalid={!version.trim() && localError}
        />
      </div>

      <div className="mb-3">
        <Form.Label>Version Code (android)</Form.Label>
        <Form.Control
          value={versionCode}
          onChange={(e) => setVersionCode(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. 102"
          required
          isInvalid={!versionCode.trim() && localError}
        />
        <Form.Text className="text-gray small">
          Android versionCode must be higher than previous release.
        </Form.Text>
      </div>

      <div className="mb-3">
        <Form.Label>Build Number (ios)</Form.Label>
        <Form.Control
          value={buildNumber}
          onChange={(e) => setBuildNumber(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. 15"
          required
          isInvalid={!buildNumber && localError}
        />
        <Form.Text className="text-gray small">
          iOS build number must be higher than the previous release.
        </Form.Text>
      </div>

      <div className="mb-3">
        <Form.Label>Apple Store URL</Form.Label>
        <Form.Control
          value={appleStoreUrl}
          onChange={(e) => setAppleStoreUrl(e.target.value)}
          disabled={isSubmitting}
          placeholder="https://apps.apple.com/app/id123456789"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Android Store URL</Form.Label>
        <Form.Control
          value={androidStoreUrl}
          onChange={(e) => setAndroidStoreUrl(e.target.value)}
          disabled={isSubmitting}
          placeholder="https://play.google.com/store/apps/details?id=com.example.app"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Effective from</Form.Label>
        <Form.Control
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="mb-3">
        <Form.Label>Update Title</Form.Label>
        <Form.Control
          value={updateTitle}
          onChange={(e) => setUpdateTitle(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. Critical Security Update"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Update Message</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          placeholder="Describe what's new in this release..."
          required
        />
      </div>

      <div className="mb-3 d-flex gap-5">
        <Form.Check
          type="checkbox"
          label="Force users to update"
          checked={forceUpdate}
          onChange={(e) => setForceUpdate(e.target.checked)}
          disabled={isSubmitting}
        />

        <Form.Check
          type="checkbox"
          label="Minimum supported version"
          checked={isMinimumSupported}
          onChange={(e) => setIsMinimumSupported(e.target.checked)}
          disabled={isSubmitting}
        />

        <Form.Check
          type="checkbox"
          label="Mark as recommended version"
          checked={isRecommended}
          onChange={(e) => setIsRecommended(e.target.checked)}
          disabled={isSubmitting}
        />
      </div>

      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          type="button"
          className="text-sm"
        >
          Cancel
        </Button>

        <Button
          variant="success"
          type="submit"
          disabled={loading || isSubmitting || isFormInvalid}
          className="text-sm"
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Add App Version"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default AddAppVersionForm;
